/* eslint-disable */
import axios from "axios";
import { getAccessToken, getRefreshToken, setJWTTokens, getTokenExp, clearJWTTokens } from "./tokens";


const api = axios.create();

api.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;

const isDirectAccessLink = () => {
  const currentPath = window.location.pathname;
  const currentHash = window.location.hash;
  
  const directAccessPattern = /^\/parents\/[^\/]+\/[^\/]+$/;
  const hasDirectAccessParams = directAccessPattern.test(currentPath);
  
  const hasAccessUidInHash = currentHash && currentHash.includes('#access_uid=');
  
  const urlParams = new URLSearchParams(window.location.search);
  const referrer = urlParams.get('referrer');
  const isAccessReferrer = referrer === 'access';
  
  const isHomeWithAccessUid = currentPath === '/parents/home' && hasAccessUidInHash;
  
  return hasDirectAccessParams || hasAccessUidInHash || isAccessReferrer || isHomeWithAccessUid;
};

// Utility function to check if the failed request is for a public endpoint
const isPublicEndpoint = (url) => {
  if (!url) return false;
  
  const publicEndpoints = [
    '/api/v6/access-card/accesses/',  
    '/api/v6/access-card/access_cards_all/',  
  ];
  
  return publicEndpoints.some(endpoint => url.includes(endpoint));
};

let isRefreshing = false;
let refreshSubscribers = [];
let refreshTimerId = null;

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach(cb => cb(newToken));
  refreshSubscribers = [];
};
export const scheduleAccessTokenRefresh = () => {
  const tokenExpSec = Number(getTokenExp());
  if (!tokenExpSec || Number.isNaN(tokenExpSec)) {
    return;
  }
  const refreshInMs = tokenExpSec * 1000 - 10000;
  if (refreshTimerId) {
    clearTimeout(refreshTimerId);
    refreshTimerId = null;
  }
  if (refreshInMs <= 0) {
    triggerRefreshNow();
    return;
  }
  refreshTimerId = setTimeout(() => {
    triggerRefreshNow();
  }, refreshInMs);
};

const triggerRefreshNow = async () => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    // If user came from direct access link, just clear tokens and stay on current page
    if (isDirectAccessLink()) {
      clearJWTTokens();
      return;
    }
    window.location.href = "/parents/login";
    return;
  }
  try {
    const response = await axios.post(`${api.defaults.baseURL}/agency/jwt/token/refresh/`, {
      refresh: refreshToken,
    });
    const newAccessToken = response.data.access;
    setJWTTokens(response.data);
    api.defaults.headers.common["Authorization"] = `JWT ${newAccessToken}`;
    onRefreshed(newAccessToken);
    scheduleAccessTokenRefresh();
  } catch (err) {
    // If user came from direct access link, just clear tokens and stay on current page
    if (isDirectAccessLink()) {
      clearJWTTokens();
      return;
    }
    window.location.href = "/parents/login";
  }
};


api.interceptors.request.use(
  config => {
    const token = getAccessToken();
    if (token) {
      config.headers["Authorization"] = `JWT ${token}`;
    }
    return config;
  },
  error => Promise.reject(error),
);

// Utility function to check if the request is a login/authentication attempt
const isLoginEndpoint = (url) => {
  if (!url) return false;
  
  const loginEndpoints = [
    '/jwt/token/',
    '/agency/jwt/token/',
  ];
  
  return loginEndpoints.some(endpoint => url.includes(endpoint));
};

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // If this is a login endpoint, don't try to refresh token or redirect
      // Just reject the error and let the login component handle it
      if (isLoginEndpoint(originalRequest.url)) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        // If user came from direct access link and this is not a public endpoint, 
        // just clear tokens and reject the request
        if (isDirectAccessLink() && !isPublicEndpoint(originalRequest.url)) {
          clearJWTTokens();
          return Promise.reject(error);
        }
        window.location.href = "/parents/login";
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh(token => {
            originalRequest.headers["Authorization"] = `JWT ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        const response = await axios.post(`${api.defaults.baseURL}/agency/jwt/token/refresh/`, {
          refresh: refreshToken,
        });

        const newAccessToken = response.data.access;
        setJWTTokens(response.data);
        api.defaults.headers.common["Authorization"] = `JWT ${newAccessToken}`;
        onRefreshed(newAccessToken);
        isRefreshing = false;

        scheduleAccessTokenRefresh();

        originalRequest.headers["Authorization"] = `JWT ${newAccessToken}`;
        return api(originalRequest);
      } catch (err) {
        console.log("Api error ", err);
        isRefreshing = false;
        
        // If user came from direct access link and this is not a public endpoint,
        // just clear tokens and reject the request
        if (isDirectAccessLink() && !isPublicEndpoint(originalRequest.url)) {
          clearJWTTokens();
          return Promise.reject(err);
        }
        
        window.location.href = "/parents/login";
        console.log("Login page should have displayed");
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  },
);

export default api;
